/**
 * LLM-facing node catalog — generated from UniversalNodeRegistry, never
 * hand-written (the previous static catalog drifted: wrong handles, invented
 * node types).
 *
 * Tier 1 (`generateNodeCatalog`): one line per node, always in the system
 * prompt. Deterministic output so the prompt-cache prefix stays warm (spec §T1).
 * Tier 2 (`describeNodeTypes`): full parameter/handle detail, fetched on
 * demand via the get_node_specs tool (spec §T2).
 */
import { UniversalNodeRegistry } from '@/features/backtest/schemas/universal-node-schema'
import { catalogExcludedTypes } from '@/features/backtest/schemas/compound'
import { offeredOutputs } from '@/features/backtest/schemas/input-activity'
import type {
  UniversalNodeSchema,
  ParameterDefinition,
} from '@/features/backtest/schemas/universal-node-schema'
import type { Recipe } from '@/features/backtest/utils/recipe-types'

/** Rough token estimate (chars/4). Real usage is recorded by the eval harness. */
export function estimateTokens(text: string): number {
  return Math.ceil(text.length / 4)
}

const CATEGORY_ORDER = [
  'data',
  'indicators',
  'logic',
  'math',
  'filter',
  'entry-exit-logic',
  'utilities',
  'debug',
  'presets',
] as const

const CATEGORY_TITLES: Record<string, string> = {
  data: 'Data Sources',
  indicators: 'Indicators',
  logic: 'Logic',
  math: 'Math',
  filter: 'Filters',
  'entry-exit-logic': 'Strategy (Entry/Exit/Risk)',
  utilities: 'Utilities',
  debug: 'Debug',
  presets: 'Presets',
}

function paramSummary(p: ParameterDefinition): string {
  return `${p.name}=${JSON.stringify(p.default).replace(/"/g, '')}`
}

function inputTypesSummary(schema: UniversalNodeSchema): string {
  if (schema.inputs.length === 0) {
    return schema.inputHandler?.type === 'multi' ? 'multi' : '-'
  }
  const types = new Set<string>()
  for (const i of schema.inputs) for (const t of i.acceptedTypes) types.add(t)
  return [...types].sort().join('|') || '-'
}

// A retired output (the Condition's False) is not offered: a saved wire from
// it still runs, but nothing should start one.
function outputsSummary(schema: UniversalNodeSchema): string {
  return (
    offeredOutputs(schema)
      .map((o) => `${o.id}:${o.passthrough ? 'passthrough' : o.type}`)
      .join(', ') || '-'
  )
}

function catalogLine(schema: UniversalNodeSchema): string {
  const params = schema.parameters.map(paramSummary).join(', ')
  const desc = schema.description || schema.displayName
  return `- ${schema.type}(${params}) :: in[${inputTypesSummary(schema)}] → ${outputsSummary(schema)} — ${desc}`
}

export function generateNodeCatalog(): string {
  const byCategory = new Map<string, UniversalNodeSchema[]>()
  const excluded = catalogExcludedTypes('ai')
  for (const schema of UniversalNodeRegistry.getAllSchemas()) {
    if (schema.deprecated) continue
    // Each schema says whether the AI sees it (schemas/compound.ts). Out: the
    // editor-only Report and Universe (which tickers and days to trade belongs
    // to the selection layer), and the trade nodes the Trade Manager stands
    // for (Simple Trade, Advanced Trade and its Entry Rules, Stop, Position
    // Sizing and Trailing Stop; Target stays for Multi Target legs): the AI
    // builds the trade side with the Trade Manager since the 2026-10-06 swap
    // (docs/superpowers/specs/2026-10-05-backtest-node-first-design.md).
    if (excluded.has(schema.type)) continue
    const list = byCategory.get(schema.category) ?? []
    list.push(schema)
    byCategory.set(schema.category, list)
  }

  const sections: string[] = ['## Available backtest node types']
  const seen = new Set<string>()
  const categories = [
    ...CATEGORY_ORDER.filter((c) => byCategory.has(c)),
    ...[...byCategory.keys()]
      .filter((c) => !(CATEGORY_ORDER as readonly string[]).includes(c))
      .sort(),
  ]
  for (const category of categories) {
    if (seen.has(category)) continue
    seen.add(category)
    const schemas = byCategory.get(category)!
    schemas.sort((a, b) => (a.type < b.type ? -1 : 1))
    sections.push(`**${CATEGORY_TITLES[category] ?? category}:**`)
    sections.push(schemas.map(catalogLine).join('\n'))
  }

  sections.push(RECIPE_INSTRUCTIONS)
  sections.push(OP_LANGUAGE_INSTRUCTIONS)
  return sections.join('\n\n')
}

// ── Tier 2: on-demand detail ────────────────────────────────

function paramDetail(p: ParameterDefinition): string {
  if (p.type === 'select') {
    return `${p.name}: select(${p.options.map((o) => String(o.value)).join('|')}) = ${String(p.default)}`
  }
  if (p.type === 'int' || p.type === 'float') {
    const range =
      p.min !== undefined || p.max !== undefined ? ` ${p.min ?? ''}..${p.max ?? ''}` : ''
    return `${p.name}: ${p.type}${range} = ${p.default}`
  }
  return `${p.name}: ${p.type} = ${JSON.stringify(p.default)}`
}

function describeOne(schema: UniversalNodeSchema): string {
  const lines: string[] = [`### ${schema.type} — ${schema.displayName}`]
  if (schema.description) lines.push(schema.description)

  if (schema.parameters.length > 0) {
    lines.push(`params: ${schema.parameters.map(paramDetail).join('; ')}`)
  }

  // A retired socket (the Trade Manager's Window) takes no new wire: not offered.
  const offered = schema.inputs.filter((i) => i.retired === undefined)
  if (offered.length > 0) {
    const inputs = offered.map((i) => {
      const staticNote = i.discriminatedUnion ? ', accepts literal static value' : ''
      const optionalNote = i.optional ? ', optional' : ''
      return `${i.id} (${i.acceptedTypes.join('|')}${staticNote}${optionalNote})`
    })
    lines.push(`inputs: ${inputs.join('; ')}`)
  } else if (schema.inputHandler?.type === 'multi') {
    const { minInputs = 2, maxInputs = 8 } = schema.inputHandler
    lines.push(`inputs: dynamic input_1..input_N (${minInputs}-${maxInputs})`)
  }

  if (schema.outputs.length > 0) {
    lines.push(
      `outputs: ${offeredOutputs(schema)
        .map((o) => `${o.id}:${o.type}`)
        .join(', ')}`,
    )
    if (schema.outputs.some((o) => o.type === 'ohlcv')) {
      lines.push(`ohlcv field refs: "<stepId>.close" (.open/.high/.low/.close/.volume)`)
    }
  }

  if (schema.inputHandler) {
    const { type, minInputs } = schema.inputHandler
    lines.push(
      `arity: ${type}${type === 'multi' ? ` (min ${minInputs ?? 2})` : type === 'dual' ? ' (both inputs required)' : type === 'all' ? ' (every input required)' : ' (input required)'}`,
    )
  }

  return lines.join('\n')
}

export function describeNodeTypes(types: string[]): string {
  const blocks: string[] = []
  for (const raw of types) {
    const schema = UniversalNodeRegistry.getSchemaResolved(raw)
    if (!schema) {
      blocks.push(`Unknown type "${raw}" — check the catalog list for valid types.`)
      continue
    }
    blocks.push(describeOne(schema))
  }
  return blocks.join('\n\n')
}

// ── Recipe instructions + executable example ────────────────

/**
 * The example embedded in the system prompt. Exported so a unit test builds
 * and validates it against the live engine — the prompt cannot drift from
 * the registry again.
 */
export const PROMPT_EXAMPLE_RECIPE: Recipe = [
  { id: 'price', type: 'priceData', params: { timeframe: '5m' } },
  {
    id: 'fast_sma',
    type: 'simplemovingaverage',
    params: { period: 10 },
    inputs: { input_1: 'price.close' },
  },
  {
    id: 'slow_sma',
    type: 'simplemovingaverage',
    params: { period: 50 },
    inputs: { input_1: 'price.close' },
  },
  {
    id: 'cross',
    type: 'crossover',
    inputs: { input_1: 'fast_sma.sma', input_2: 'slow_sma.sma' },
  },
  {
    id: 'tm',
    type: 'tradeManager',
    params: { stopValue: 1.5, targetValue: 3 },
    inputs: { trigger: 'cross.signal' },
  },
]

const RECIPE_INSTRUCTIONS = `## Building strategies with create_strategy_from_recipe (PREFERRED)

Use create_strategy_from_recipe with a compact recipe array instead of calling add_node/connect_nodes repeatedly. For full parameter/handle detail on specific types, call get_node_specs first.

Recipe step format:
{ "id": "unique_name", "type": "nodeType", "params": { "key": value }, "inputs": { "targetHandle": "sourceId.outputHandle" } }

Connection rules:
- "sourceId.outputHandle" connects to the specified output (e.g. "fast_sma.sma")
- OHLCV fields: "price.close", "price.volume" etc. (auto-resolves to the ohlcv output + field selection)
- Static values: pass a number/boolean literal directly, e.g. "inputs": { "input_2": 30 }
- Omit params that match defaults — they are filled automatically

Example — SMA crossover:
${JSON.stringify(PROMPT_EXAMPLE_RECIPE)}

Pattern:
1. Start with priceData as the data source
2. Connect indicators to priceData via OHLCV field refs
3. Use condition/crossover/merge for signal logic
4. Wire the entry signal into ONE tradeManager's trigger and set its exits and sizing as params. Computed price level: targetType/stopType "Static Value" + wire targetLevel/stopLevel. Condition exit: targetType "Condition" + targetWhen. Scale-outs: targetType "Custom" + a multiTarget into targetCustom.`

const OP_LANGUAGE_INSTRUCTIONS = `## Editing a strategy with apply_graph_ops (PREFERRED for edits)

Batch edit ops applied to the current graph in ONE call — transactional (nothing changes if any op fails). Prefer for any multi-step edit; legacy add_node/connect_nodes/set_node_parameters are for a single trivial change only. Ref nodes by read_current_graph step id; refs are "stepId.handle" ("price.close" for OHLCV).

Ops:
- { "op": "add_node", "id": "rsi", "type": "relativestrengthindex", "params": { "period": 14 } }
- { "op": "set_params", "id": "rsi", "params": { "period": 10 } }
- { "op": "connect", "from": "rsi.rsi", "to": "cond.input_1" }
- { "op": "disconnect", "to": "cond.input_1" }
- { "op": "remove_node", "id": "rsi" }
- { "op": "insert_between", "id": "smooth", "type": "exponentialmovingaverage", "from": "pricedata", "to": "condition" }
- { "op": "replace_node", "id": "fast_sma", "type": "exponentialmovingaverage" }

Example — tighten RSI and widen the exits on the trade manager, one batch:
[{"op":"set_params","id":"relativestrengthindex","params":{"period":10}},{"op":"set_params","id":"trademanager","params":{"stopValue":1.5,"targetValue":3}}]`
